import { useCallback } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { message } from "antd";
import sdk, { GraphQLError } from "@/sdk";
import { useSearchParam } from "@/utils/url-state";

export const useMSCDAccounts = () => {
  const [pageParam, { set: setPageParam }] = useSearchParam("page");
  const [limitParam, { set: setLimitParam }] = useSearchParam("limit");
  const [searchParam, { set: setSearchParam, unset: unsetSearchParam }] =
    useSearchParam("search");

  const page = pageParam ? parseInt(pageParam, 10) || 0 : 0;
  const limit = limitParam ? parseInt(limitParam, 10) || 20 : 20;
  const search = searchParam || undefined;

  const setPage = useCallback(
    (p: number) => setPageParam(String(p)),
    [setPageParam],
  );
  const setLimit = useCallback(
    (l: number) => setLimitParam(String(l)),
    [setLimitParam],
  );
  const setSearch = useCallback(
    (s: string | undefined) => (s ? setSearchParam(s) : unsetSearchParam()),
    [setSearchParam, unsetSearchParam],
  );

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["mscdAccounts", page, limit, search],
    queryFn: () =>
      sdk.getMSCDApiAccounts({
        page,
        limit,
        filter: search ? { search } : undefined,
      }),
    select: (data) => data.getMSCDApiAccounts,
  });

  const syncMutation = useMutation({
    mutationFn: (params: { beginDate?: string; endDate?: string }) =>
      sdk.syncMSCDAccounts(params),
    onSuccess: (data) => {
      const result = data.syncMSCDAccounts;
      message.success(
        `Синхрончилол амжилттай: ${result.synced} синхрон, ${result.upserted} нэмэгдсэн, ${result.modified} шинэчлэгдсэн`,
      );
      refetch();
    },
    onError: (err) => {
      message.error(
        (err as GraphQLError).response?.errors?.[0]?.message ?? "Алдаа гарлаа",
      );
    },
  });

  const exportMutation = useMutation({
    mutationFn: () =>
      (sdk as any).exportMSCDApiAccountsExcel() as Promise<{
        exportMSCDApiAccountsExcel: { reportUrl: string };
      }>,
    onSuccess: (data) => {
      const reportUrl = data.exportMSCDApiAccountsExcel.reportUrl;
      window.open(reportUrl, "_blank");
      message.success("Excel файл амжилттай үүслээ");
    },
    onError: (err) => {
      message.error(
        (err as GraphQLError).response?.errors?.[0]?.message ?? "Алдаа гарлаа",
      );
    },
  });

  const dispatchMutation = useMutation({
    mutationFn: (params: { startDate?: number }) =>
      (sdk as any).dispatchAccountCreationForDepositors(params) as Promise<{
        dispatchAccountCreationForDepositors: {
          dispatched: number;
          skipped: number;
        };
      }>,
    onSuccess: (data) => {
      const result = data.dispatchAccountCreationForDepositors;
      message.success(
        `Амжилттай: ${result.dispatched} илгээсэн, ${result.skipped} алгассан`,
      );
      refetch();
    },
    onError: (err) => {
      message.error(
        (err as GraphQLError).response?.errors?.[0]?.message ?? "Алдаа гарлаа",
      );
    },
  });

  return {
    data,
    loading: isLoading,
    page,
    limit,
    search,
    exportMutation,
    dispatchMutation,
    setPage,
    setLimit,
    setSearch,
    refetch,
    syncMutation,
  };
};
